import { Controller, Control, FieldErrors } from 'react-hook-form';
import { LoanFormData } from '@/schemas/loanSchema';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { getErrorMessage } from '@/components/formErrorMessage';
import USFormattedNumberInput from '@/components/ui/USFormattedNumberInput';

interface Props {
  control: Control<LoanFormData>;
  errors: FieldErrors<LoanFormData>;
}

export const InterestTermsSection = ({ control, errors }: Props) => (
  <div className="p-8 mb-8" data-test-id="interest-terms-container">
    <h3 className="section-title" data-test-id="interest-terms-title">
      Loan Interest Terms
    </h3>

    {/* Row 1: Interest Rate Type & Fixed Rate Coupon */}
    <div className="grid-2 mb-8" data-test-id="interest-terms-row-1">
      <div data-test-id="interest-type-field">
        <Label
          className="form-label loanform-label"
          htmlFor="interest_terms.interest_type"
          data-test-id="interest-type-label"
        >
          Interest Rate Type*
        </Label>
        <Controller
          name="interest_terms.interest_type"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              data-test-id="interest-type-select"
            >
              <SelectTrigger
                className="loanform-select-trigger loanform-select-bg loanform-input-border"
                error={getErrorMessage(errors, 'interest_terms.interest_type')}
                data-test-id="interest-type-trigger"
              >
                <SelectValue
                  placeholder="Select interest type"
                  data-test-id="interest-type-value"
                />
              </SelectTrigger>
              <SelectContent
                className="loanform-select-content loanform-select-bg loanform-border"
                data-test-id="interest-type-content"
              >
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Fixed"
                  data-test-id="interest-type-option-fixed"
                >
                  Fixed
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="Floating"
                  data-test-id="interest-type-option-floating"
                >
                  Floating
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div data-test-id="fixed-rate-coupon-field">
        <Label
          className="form-label loanform-label"
          htmlFor="interest_terms.fixed_rate_coupon"
          data-test-id="fixed-rate-coupon-label"
        >
          Fixed Rate Coupon*
        </Label>
        <Controller
          name="interest_terms.fixed_rate_coupon"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="percent"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="fixed-rate-coupon-input"
            />
          )}
        />
      </div>
    </div>

    {/* Row 2: Index Cap & Index Floor */}
    <div className="grid-2 mb-8" data-test-id="interest-terms-row-2">
      <div data-test-id="index-cap-field">
        <Label
          className="form-label loanform-label"
          htmlFor="interest_terms.index_cap"
          data-test-id="index-cap-label"
        >
          Index Cap*
        </Label>
        <Controller
          name="interest_terms.index_cap"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="percent"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="index-cap-input"
            />
          )}
        />
      </div>
      <div data-test-id="index-floor-field">
        <Label
          className="form-label loanform-label"
          htmlFor="interest_terms.index_floor"
          data-test-id="index-floor-label"
        >
          Index Floor*
        </Label>
        <Controller
          name="interest_terms.index_floor"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="percent"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="index-floor-input"
            />
          )}
        />
      </div>
    </div>

    {/* Row 3: Floating Rate Index & Floating Rate Margin */}
    <div className="grid-2" data-test-id="interest-terms-row-3">
      <div data-test-id="floating-rate-index-field">
        <Label
          className="form-label loanform-label"
          htmlFor="interest_terms.floating_rate_index"
          data-test-id="floating-rate-index-label"
        >
          Floating Rate Index*
        </Label>
        <Controller
          name="interest_terms.floating_rate_index"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value ?? ''}
              data-test-id="floating-rate-index-select"
            >
              <SelectTrigger
                className="loanform-select-trigger loanform-select-bg loanform-input-border"
                error={getErrorMessage(
                  errors,
                  'interest_terms.floating_rate_index'
                )}
                data-test-id="floating-rate-index-trigger"
              >
                <SelectValue
                  placeholder="Select index"
                  data-test-id="floating-rate-index-value"
                />
              </SelectTrigger>
              <SelectContent
                className="loanform-select-content loanform-select-bg loanform-border"
                data-test-id="floating-rate-index-content"
              >
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="EURIBOR1M"
                  data-test-id="floating-rate-index-option-euribor1m"
                >
                  EURIBOR1M
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="EURIBOR3M"
                  data-test-id="floating-rate-index-option-euribor3m"
                >
                  EURIBOR3M
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="EURIBOR6M"
                  data-test-id="floating-rate-index-option-euribor6m"
                >
                  EURIBOR6M
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="SOFR1M"
                  data-test-id="floating-rate-index-option-sofr1m"
                >
                  SOFR1M
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="SONIA1M"
                  data-test-id="floating-rate-index-option-sonia1m"
                >
                  SONIA1M
                </SelectItem>
                <SelectItem
                  className="loanform-select-item loanform-select-hover"
                  value="SONIA3M"
                  data-test-id="floating-rate-index-option-sonia3m"
                >
                  SONIA3M
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>
      <div data-test-id="floating-rate-margin-field">
        <Label
          className="form-label loanform-label"
          htmlFor="interest_terms.floating_rate_margin"
          data-test-id="floating-rate-margin-label"
        >
          Floating Rate Margin*
        </Label>
        <Controller
          name="interest_terms.floating_rate_margin"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="percent"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="floating-rate-margin-input"
            />
          )}
        />
      </div>
    </div>
  </div>
);
